import {
    useEffect,
    useRef,
    type FC
} from "react";
import {
    Pressable,
    Platform,
    View
} from "react-native";
import type IBubbleReactionsChipProps from "./type";
import {
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitTheme
} from "../../../../../../core/hooks";
import {
    useInteractionState
} from "../../../../../../helpers/interaction";
import {
    usePointerType
} from "../../../../../../helpers/pointer";
import BadgeCounter from "../../../../../badge/internal/counter";
import StateLayer from "../../../../../stateLayer";
import stylesheet from "../../stylesheet";
import Text from "../../../../../text";

const BubbleReactionsChip: FC<IBubbleReactionsChipProps> = ({
    customTheme,
    countTitle,
    onHoverOut,
    onPressOut,
    onHoverIn,
    onPressIn,
    isToggle,
    onFocus,
    onPress,
    onBlur,
    count,
    emoji,
    isOwn,
    style,
    title,
    ...props
}) => {
    const {
        controlHeights,
        shapeConfigs,
        touchTargets,
        shadowAlpha,
        elevations,
        typography,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const pointerType = usePointerType();

    const {
        interactionHandlers,
        isFocusVisible,
        isHovered,
        isPressed
    } = useInteractionState({
        onHoverOut,
        onPressOut,
        onHoverIn,
        onPressIn,
        onFocus,
        onBlur
    });

    const previousCountRef = useRef(count);

    const isPressable = !!onPress;

    const getCounterDirection = () => {
        if(count === previousCountRef.current) {
            return 0;
        }

        return count > previousCountRef.current ? 1 : -1;
    };

    const counterDirection = getCounterDirection();

    useEffect(() => {
        previousCountRef.current = count;
    }, [
        count
    ]);

    const {
        surface: surfaceDynamicStyle,
        emoji: emojiDynamicStyle,
        chip: chipDynamicStyle
    } = useStyles({
        controlHeights,
        isFocusVisible,
        shapeConfigs,
        touchTargets,
        isPressable,
        pointerType,
        shadowAlpha,
        elevations,
        typography,
        radiuses,
        borders,
        colors,
        spaces,
        isOwn
    });

    const textColor = isOwn ? colors.content.text.high : colors.content.text.mid;

    const renderSurface = () => {
        return <View
            style={[
                stylesheet.surface,
                surfaceDynamicStyle
            ]}
        >
            {
                isPressable ?
                    <StateLayer
                        borderRadius={radiuses.chip}
                        borderWidth={borders.line}
                        customTheme={customTheme}
                        isHovered={isHovered}
                        isPressed={isPressed}
                    />
                :
                    null
            }
            <Text
                customTheme={customTheme}
                style={emojiDynamicStyle}
                variant="bodyMediumSize"
                customColor={textColor}
                aria-hidden={true}
            >
                {emoji}
            </Text>
            <View
                aria-hidden={true}
            >
                <BadgeCounter
                    titleVariant="labelSmallSize"
                    direction={counterDirection}
                    customTheme={customTheme}
                    customColor={textColor}
                    title={countTitle}
                />
            </View>
        </View>;
    };

    if(!isPressable) {
        return <View
            style={[
                style,
                stylesheet.chip,
                chipDynamicStyle
            ]}
            aria-label={title}
            role="img"
        >
            {renderSurface()}
        </View>;
    }

    const webProps = Platform.OS === "web" && !isToggle ? {
        "aria-haspopup": "dialog"
    } : {};

    return <Pressable
        {...props}
        {...webProps}
        {...interactionHandlers}
        aria-pressed={isToggle ? isOwn : undefined}
        accessibilityLabel={title}
        style={[
            style,
            stylesheet.chip,
            chipDynamicStyle
        ]}
        aria-label={title}
        onPress={onPress}
        role="button"
    >
        {renderSurface()}
    </Pressable>;
};
export default BubbleReactionsChip;
